// Hand state + hand-drag flow, extracted from GameBoard.jsx. Owns the
// local (and sandbox P2) hand arrays, the opponent-facing hand-info
// broadcast (counts + revealed cards only), and the drag pipeline that
// lifts a hand card onto the table or hands a table card back into the
// hand fan. In casual sandbox the live hand follows the Tab-swapped
// active player.

import { UI, playUI } from '../../../utils/arena/uiSounds';
import { createTargetHaloMesh } from '../../../utils/game/cardMesh';
import { resetCardOnLeavePlay, addSpellToUnitHand, removeSpellFromUnitHand, flushUnitHand, clearCastLock, tagCastLock } from '../../../utils/game/engine/core';
import { msg } from '../../../utils/game/logMessage';

// Which internal hand array a card lives in. In casual sandbox, the
// card's `rotated` flag is authoritative. P1 cards go to handCards,
// P2 cards go to handCards2. In multiplayer mode the local user
// only ever owns one side's cards so everything lands in handCards
// (the legacy single-hand path), and handCards2 stays empty.
export function handStateKeyForCard(host, cardInstance) {
  if (!host.props.isCasualSandbox) return 'handCards';
  return cardInstance.rotated ? 'handCards2' : 'handCards';
}

export function addToHand(host, cardInstance) {
  // An owner-locked spell ("Only It Can Cast") may NEVER sit in the
  // player hand. Route it back to its unit hand no matter how it got
  // here (drag-into-hand, reorder, etc.).
  if (cardInstance._castableOnlyBy) {
    addToUnitHand(host, cardInstance._castableOnlyBy, cardInstance);
    return;
  }
  // A face-down card returning to hand may be a sanitized copy we lost
  // the identity of; re-hydrate it so it doesn't sit in hand as a
  // nameless, invisible outline.
  host.identityVault.restore(cardInstance);
  const key = host._handStateKeyForCard(cardInstance);
  host.setState((state) => {
    // A hand never holds the same instance twice. A duplicate add is
    // always an upstream bug (stale hover, double fold), so drop it.
    if ((state[key] || []).some((c) => c.id === cardInstance.id)) return null;
    const next = [...(state[key] || []), cardInstance];
    // Only the *displayed* hand (active player's) needs to broadcast
    // its info. In single-player casual mode there's no opponent
    // to push to anyway, and in multiplayer addToHand is always for
    // the local player (which is always the active one).
    return { [key]: next };
  }, host.scheduleLocalSave);
}

// Route a freshly drawn owner-locked spell into its unit's hand instead
// of the player hand. The unit hand's controller is the owner unit's
// current controller (its `rotated` flag). See engine/core/unitHands.js.
export function addToUnitHand(host, ownerId, cardInstance, ownerArt = null, index = null) {
  host.identityVault.restore(cardInstance);
  // Carried-aware owner lookup: an equipped Omphalos has no mesh of its
  // own, and the bare meshes fallback silently derived controller 'p1'.
  // handing every guest-owned Omphalos to the opponent (#60). Fall back to
  // the auth mirror's card before defaulting.
  const ownerCard = host._inPlayInstanceById?.(ownerId)
    || host.meshes.get(ownerId)?.userData?.cardInstance
    || host._authMirror?.getState?.()?.cards?.[ownerId]
    || null;
  const controller = (ownerCard?.controller === 'p2' || ownerCard?.rotated) ? 'p2' : 'p1';
  // Stamp the OWNER seat once, at draw time. The draw-time controller is
  // the original owner, and the FAQ requires cards played from a unit
  // hand to return to that OWNER's cemetery / hand / decks regardless of
  // who later controls the unit. `controller` (and each spell's
  // `rotated`) follow control for CAST routing; `_ownerSeat` never moves,
  // so leave / cast cemetery routing stays owner-anchored. Set only on
  // first entry so a cancelled-drag re-add never re-stamps it.
  if (cardInstance._ownerSeat === undefined) cardInstance._ownerSeat = controller;
  // Resolve the badge art ONCE, here, and stamp it on the entry as public
  // board data. The badge then never depends on a live mesh. This matters
  // because an owner can be EQUIPPED (Omphalos worn by a site, a minion
  // carried by another) and so have no mesh of its own; a mesh-only lookup
  // would fall back to the card back. Order: caller-supplied art (resolved
  // from the live drawing source at the draw site, mesh-independent) →
  // mesh imageUrl → catalog printing by cardId. The caller-supplied art is
  // the reliable one for equipped owners.
  const ownerFull = ownerCard?.cardId ? host._getFullCard?.(ownerCard.cardId) : null;
  const art = ownerArt || ownerCard?.imageUrl || ownerFull?.printings?.[0]?.image_url || null;
  host.setState((state) => {
    const unitHands = addSpellToUnitHand(state.gameState.unitHands, ownerId, cardInstance, controller, index);
    if (art && unitHands[ownerId]) unitHands[ownerId].ownerArt = art;
    // Broadcast the FRESH map from inside the updater. Preact's setState is
    // async, so a sync call after it would read the stale (pre-add) state and
    host._syncUnitHands?.(unitHands);
    return { gameState: { ...state.gameState, unitHands } };
  }, host.scheduleLocalSave);
}

export function removeFromUnitHand(host, ownerId, spellId, localOnly = false) {
  host.setState((state) => {
    const unitHands = removeSpellFromUnitHand(state.gameState.unitHands, ownerId, spellId);
    if (!localOnly) host._syncUnitHands?.(unitHands); // fresh map (see addToUnitHand)
    return { gameState: { ...state.gameState, unitHands } };
  }, host.scheduleLocalSave);
}

// The unit hand's controller is a DERIVED cache of the owner card's control
// (`rotated`). The single synced source of truth. Re-derive it, and each
// held spell's `rotated` (cast/cemetery routing reads it), from the live
// owner card so the hand always matches the side the card sits on and can
// never split-brain across clients. Returns true if anything changed so the
// caller can trigger a re-render. Owner not in the realm → entry left as-is
// (it is flushed on leave-play). Runs on every recompute (every control
// change), on both clients, so a separately-synced controller is unnecessary.
export function reconcileUnitHandControllers(host) {
  const unitHands = host.state.gameState?.unitHands;
  if (!unitHands) return false;
  let changed = false;
  for (const [ownerId, entry] of Object.entries(unitHands)) {
    // Carried-aware: an equipped owner (Omphalos on its site) has no mesh.
    // skipping it froze a wrong controller forever (#60).
    const owner = host._inPlayInstanceById?.(ownerId)
      || host.meshes.get(ownerId)?.userData?.cardInstance;
    if (!owner) continue;
    const controller = (owner.controller === 'p2' || owner.rotated) ? 'p2' : 'p1';
    if (entry.controller !== controller) { entry.controller = controller; changed = true; }
    for (const s of entry.spells || []) {
      if (s.rotated !== owner.rotated) { s.rotated = owner.rotated; changed = true; }
      // Controlling the hand reveals its current cards to this seat, and the
      // knowledge sticks (sticky per-seat reveal). Cards drawn later start
      // unseen by the other seat until it next controls. See sanitizeUnitHandsForSeat.
      if (s._controllerOnly) continue;
      if (!s._seenBy) s._seenBy = [];
      if (!s._seenBy.includes(controller)) { s._seenBy.push(controller); changed = true; }
    }
  }
  if (changed) host.setState?.((s) => ({ gameState: { ...s.gameState } }), host.scheduleLocalSave);
  return changed;
}

// The owner unit left the realm: drop its unit hand and flush any
// remaining spells to the cemetery (codex: "If the special spellcaster
// leaves the realm, any remaining spells go to the cemetery"). Per the
// Morgana FAQ the cemetery is the OWNER's, not the current controller's,
// so sendCardToPile routes by each spell's `_ownerSeat`. `_ownerSeat`
// must be READ (by findDestinationPile inside sendCardToPile) BEFORE it
// is cleared, so the clear happens AFTER the routing call.
export function flushUnitHandOnLeave(host, ownerId, { mirrorSingleWriter = false } = {}) {
  const entry = host.state.gameState.unitHands?.[ownerId];
  if (!entry) return;
  // A mirror-fold exit runs on BOTH clients; only the hand's controller
  // routes the spells to the cemetery (the server reducer already dropped
  // the unitHands entry). Otherwise each client emitted its own
  // MOVE_TO_ZONE per spell and the cemetery held duplicates.
  if (mirrorSingleWriter) {
    const localSeat = host.getActivePlayerRotated() ? 'p2' : 'p1';
    if ((entry.controller || null) !== localSeat) return;
  }
  const { unitHands, spells } = flushUnitHand(host.state.gameState.unitHands, ownerId);
  host.setState((state) => ({ gameState: { ...state.gameState, unitHands } }), host.scheduleLocalSave);
  host._syncUnitHands?.(unitHands); // fresh post-flush map (this.state is stale after setState)
  for (const spell of spells) {
    clearCastLock(spell);
    if (spell._returnToOwnerHand) continue; // server restores Pith Imp's held spells
    host.sendCardToPile(spell, 'Cemetery');
    delete spell._ownerSeat;
  }
}

// Drag a spell out of a unit-hand badge fan to cast it. Mirrors the
// hand → table commit (commitHandDragToTable) but sources from the unit
// hand: pull the spell out, stage it on the table, and hand off to the
// shared 3D drag. The drop fires _finalizeFreshPlayCommit (magic →
// storyline → cemetery; everything else enters the realm), and a cancel
// (drag back into the hand area) returns it to the unit hand via the
// _castableOnlyBy guard in handOffDragToHand.
export function beginUnitHandDrag(host, event, spell, ownerId) {
  event.preventDefault();
  if (event.button !== 0) return;
  playUI('snd-card-hand-click.wav', { volume: 0.6 });
  // Remove from the unit hand the instant the drag starts, so the spell is
  // ever only in ONE place. The table (dragging) → board/cemetery (commit)
  // or back to the unit hand (cancel via returnDragToUnitHand). This makes
  // a "played card still showing in the hand" duplicate impossible. The
  // badge stays visible as a drop target because the component renders the
  // dragging owner's badge (host._unitHandDrag) even at zero spells.
  removeFromUnitHand(host, ownerId, spell.id, !!spell._returnToOwnerHand);
  const point = host.scene.raycastTablePoint(event);
  if (point) { spell.x = point.x; spell.z = point.z; }
  host.addCardToTable(spell, true, { staged: true });
  // Explicit drag-origin marker. The drop/handoff guards key off this, NOT
  // `_castableOnlyBy` (which resetCardOnLeavePlay wipes the instant the
  // card leaves the table. Reading it afterward let the player-hand splice
  // run and duplicated the spell into both hands).
  host._unitHandDrag = { ownerId, spellId: spell.id };
  const mesh = host.meshes.get(spell.id);
  if (mesh) {
    host.startCardDrag({
      primaryMesh: mesh,
      dragIds: new Set([spell.id]),
      anchorX: mesh.position.x,
      anchorZ: mesh.position.z,
      freshPlay: true,
    });
  }
}

// Mid-drag cancel: an owner-locked spell dragged back over its origin
// unit badge returns to that unit hand instead of casting. Mirrors the
// freshPlay-cancel cleanup in handOffDragToHand, but routes to the unit
// hand (the spell never belonged in the player hand).
export function returnDragToUnitHand(host, cardInstance, index = null) {
  // Owner from the explicit drag marker (survives the lock-wipe);
  // fall back to the lock if somehow unset.
  const ownerId = host._unitHandDrag?.ownerId || cardInstance._castableOnlyBy;
  host._unitHandDrag = null;
  host.dragging = null;
  host.clearCemeteryDragHover();
  host.clearDeckDragHover();
  host.removeCardFromTable(cardInstance, true, { staged: true });
  if (ownerId) {
    tagCastLock(cardInstance, ownerId); // restore the lock the reset wiped
    host.addToUnitHand(ownerId, cardInstance, null, index); // index → reorder slot
  }
  host.setState({ handDragIndex: -1, hoveredHandIndex: -1 });
}

// Amber glow on the in-play owner unit while its unit hand is open, so it's
// obvious which card the hand belongs to. Reuses the card-ring halo mesh;
// attached once per owner, toggled visible. Pass null to clear.
const UNIT_HAND_GLOW_COLOR = 0xffbf40;
// The mesh whose equippedCards carries `ownerId`, or null. Lets the glow
// land on the bearer when the caster is carried (and thus mesh-less).
function bearerMeshOf(host, ownerId) {
  for (const [, mesh] of host.meshes) {
    if (mesh?.userData?.cardInstance?.equippedCards?.some((c) => c.id === ownerId)) return mesh;
  }
  return null;
}
export function setUnitHandGlow(host, ownerId) {
  const prevGlow = host._unitHandGlowMesh?.userData?._unitHandGlow;
  if (prevGlow) prevGlow.visible = false;
  host._unitHandGlowMesh = null;
  if (!ownerId) return;
  // When the owner is equipped/carried it has no table mesh of its own
  // (removeCardFromTable wiped it on equip), so glow the bearer instead.
  // that's where the caster physically sits on the mat.
  const mesh = host.meshes.get(ownerId) || bearerMeshOf(host, ownerId);
  if (!mesh) return;
  host._unitHandGlowMesh = mesh;
  let glow = mesh.userData._unitHandGlow;
  if (!glow) {
    glow = createTargetHaloMesh(UNIT_HAND_GLOW_COLOR);
    glow.position.z = 0.03;
    mesh.add(glow);
    mesh.userData._unitHandGlow = glow;
  }
  glow.visible = true;
}

export function removeFromHand(host, cardInstance) {
  // Marker consumed by addCardToTable so hand-origin transforms
  // (Animist's cast-as-Spirit) only fire for fresh hand → table
  // plays, never for clones/tokens/cemetery spawns. removeFromHand
  // is also called by hand → pile sends; the flag is harmless there
  // since it never reaches addCardToTable.
  cardInstance._fromHand = true;
  const key = host._handStateKeyForCard(cardInstance);
  host.setState((state) => {
    const next = (state[key] || []).filter((c) => c.id !== cardInstance.id);
    return { [key]: next };
  }, host.scheduleLocalSave);
}

export function isDisplayedHandKey(host, key) {
  if (!host.props.isCasualSandbox) return key === 'handCards';
  return (host.state.activePlayerNum === 2)
    ? key === 'handCards2'
    : key === 'handCards';
}

// State key for whichever hand the local user is currently
// controlling. In multiplayer: always 'handCards'. In casual
// sandbox: swaps with Tab. Used by drag/reorder/commit paths
// that need to read + write the hand without caring about
// player identity. They just operate on whichever side is live.
export function activeHandKey(host) {
  if (!host.props.isCasualSandbox) return 'handCards';
  return host.state.activePlayerNum === 2 ? 'handCards2' : 'handCards';
}

// Currently-displayed hand. Points at whichever player is active.
// Multiplayer: always handCards. Casual sandbox: alternates based
// on activePlayerNum.
export function getActiveHandCards(host) {
  if (!host.props.isCasualSandbox) return host.state.handCards;
  return host.state.activePlayerNum === 2 ? host.state.handCards2 : host.state.handCards;
}

// Hand card count for a seat ('p1' | 'p2'). In casual sandbox both
// hands are local (handCards / handCards2). In multiplayer the local
// player owns handCards; the opponent's hand is known only by count
// (opponentHand carries one entry per card). _localSeat resolves
// whether the local user is p1 or p2.
export function handCountFor(host, player) {
  if (host.props.isCasualSandbox) {
    return (player === 'p2' ? host.state.handCards2 : host.state.handCards)?.length || 0;
  }
  const local = host._localSeat();
  if (player === local) return host.state.handCards?.length || 0;
  return host.state.opponentHand?.length || 0;
}

/**
 * Begin tracking a hand card drag. Instead of immediately removing the
 * card from hand and placing it on the table, we wait and see whether
 * the user drags within the hand (reorder) or past the container edge
 * (commit to table).
 */
// --- Hand drag (reorder + place-on-table) ---
export function beginHandDrag(host, event, cardInstance, handPosition) {
  event.preventDefault();
  // Replay playback is read-only: a hand card can be inspected, never
  // dragged out and "played". Click opens the inspector instead.
  if (host.props.replayDriver) {
    if (cardInstance?.cardId) host.handleOpenLogbookMention(cardInstance.cardId);
    return;
  }
  // During the mulligan phase (of the card's own player), a
  // left-click toggles that card's mulligan flag instead of starting
  // a drag. Checking by cardInstance.rotated rather than active-
  // player keys means a P2 card is always checked against P2's
  // mulligan phase. Correct even if the active player is P1 while
  // hovering P2's hand for some reason.
  // Only the 'selecting' stage of mulligan diverts a hand click
  // into a flag toggle. In 'prompt' stage the user is asked
  // yes/no first; regular drag is still blocked because the
  // opening draw hasn't been committed yet.
  if (host._mulliganPhaseForCard(cardInstance) === 'selecting') {
    host.toggleMulliganFlag(cardInstance);
    return;
  }
  if (host._mulliganPhaseForCard(cardInstance) === 'prompt') {
    // Prompt stage: swallow the click so the user can't accidentally
    // drag the pre-mulligan hand.
    return;
  }
  // Server game-start gate: until BOTH players finish their mulligan the match
  // is not 'playing' (server-authoritative SET_MULLIGAN_DONE). Block hand drags
  // so a player who finished their own mulligan first cannot play / manipulate
  // their hand while the opponent is still mulliganing.
  if (host._authZonesEnabled?.() && host.state.matchPhase !== 'playing') return;
  const handKey = host._handStateKeyForCard(cardInstance);
  const idx = (host.state[handKey] || []).findIndex((c) => c.id === cardInstance.id);
  if (idx < 0) return;

  const container = event.currentTarget.closest('[data-tutorial]');
  const rect = container ? container.getBoundingClientRect() : null;

  host.handDrag = {
    card: cardInstance,
    originalIndex: idx,
    startX: event.clientX,
    startY: event.clientY,
    handPosition,
    containerRect: rect,
    committed: false,
  };

  host.setState({ handDragIndex: idx, hoveredHandIndex: -1 });

  document.addEventListener('mousemove', host.onHandDragMove);
  document.addEventListener('mouseup', host.onHandDragEnd);
}

export function onHandDragMove(host, event) {
  const hd = host.handDrag;
  if (!hd) return;

  const rect = hd.containerRect;

  // Cursor left the hand container. Commit card to the table
  if (rect) {
    const pastEdge = hd.handPosition === 'left'
      ? event.clientX > rect.right + 20
      : event.clientY < rect.top - 10;

    if (pastEdge) {
      host.commitHandDragToTable(event);
      return;
    }
  }

  // Reorder within hand. Target the card OWNER's hand so the
  // splice stays consistent with the owner-routed insert performed
  // by handOffDragToHand. In normal play the owner equals the
  // active player; routing off the card defends against edge cases
  // where the active player flipped mid-drag.
  const activeHandKey = host._handStateKeyForCard(hd.card);
  const cards = host.state[activeHandKey];
  const dragIdx = host.state.handDragIndex;
  if (dragIdx < 0 || !cards[dragIdx]) return;

  let targetIdx = dragIdx;

  if (rect && hd.handPosition === 'left') {
    const mouseY = event.clientY - rect.top;
    targetIdx = Math.max(0, Math.min(cards.length - 1, Math.floor(mouseY / 65)));
  } else if (rect) {
    const N = cards.length;
    const s = host.state.canvasScale;
    const arcRadius = Math.round(1800 * s);
    const maxAPC = N <= 3 ? 3 : N <= 5 ? 3 : N <= 8 ? 3 : N <= 12 ? 2 : 1.2;
    const tArcMax = N <= 5 ? 15 : N <= 8 ? 22 : N <= 12 ? 22 : 15;
    const apc = Math.min(maxAPC, tArcMax / Math.max(N - 1, 1));
    const totalArc = apc * (N - 1);

    const centerX = rect.left + rect.width / 2;
    const mouseX = event.clientX - centerX;

    let closestIdx = dragIdx;
    let closestDist = Infinity;
    for (let i = 0; i < N; i++) {
      const angleDeg = N === 1 ? 0 : -totalArc / 2 + i * apc;
      const cardX = arcRadius * Math.sin(angleDeg * Math.PI / 180);
      const dist = Math.abs(mouseX - cardX);
      if (dist < closestDist) { closestDist = dist; closestIdx = i; }
    }
    targetIdx = closestIdx;
  }

  if (targetIdx !== dragIdx) {
    // Same tick sfx used when hovering cards in the hand. Gives
    // the reorder a tactile feel that matches the rest of the hand UX.
    try { playUI(UI.HOVER, { volume: 0.4 }); } catch {}
    host.setState((state) => {
      const next = [...(state[activeHandKey] || [])];
      const [moved] = next.splice(state.handDragIndex, 1);
      next.splice(targetIdx, 0, moved);
      return { [activeHandKey]: next, handDragIndex: targetIdx };
    });
  }
}

export function onHandDragEnd(host) {
  document.removeEventListener('mousemove', host.onHandDragMove);
  document.removeEventListener('mouseup', host.onHandDragEnd);

  if (host.handDrag && !host.handDrag.committed) {
    // Card stays in hand at its (possibly reordered) position.
    // Broadcast the owner's hand, not always P1's, so sandbox
    // reorders on the away-player's hand don't leak into network
    // sync of the home player's hand.
    const handKey = host._handStateKeyForCard(host.handDrag.card);
    if (host._isDisplayedHandKey(handKey)) {
    }
  }

  host.handDrag = null;
  host.setState({ handDragIndex: -1 });
}

/**
 * The cursor has left the hand container. Commit the card to the
 * table and hand off to the shared 3D drag system.
 */
export function commitHandDragToTable(host, event) {
  const hd = host.handDrag;
  if (!hd || hd.committed) return;
  hd.committed = true;

  document.removeEventListener('mousemove', host.onHandDragMove);
  document.removeEventListener('mouseup', host.onHandDragEnd);

  playUI('snd-card-hand-click.wav', { volume: 0.6 });

  const cardInstance = hd.card;
  host.removeFromHand(cardInstance);
  host.setState({ handDragIndex: -1, hoveredHandIndex: -1 });
  host.handDrag = null;

  const point = host.scene.raycastTablePoint(event);
  if (point) {
    cardInstance.x = point.x;
    cardInstance.z = point.z;
  }
  // Stage the card on the table for a drag. Play-commit effects
  // (log, mana, affinity) fire later in handleMouseUp when the
  // drag actually ends on the table.
  host.addCardToTable(cardInstance, true, { staged: true });

  const mesh = host.meshes.get(cardInstance.id);
  if (mesh) {
    host.startCardDrag({
      primaryMesh: mesh,
      dragIds: new Set([cardInstance.id]),
      anchorX: mesh.position.x,
      anchorZ: mesh.position.z,
      freshPlay: true,
    });
  }
}

// The authoritative zone the server still holds a fresh-play card in, when
// that zone is a deck. A hand-origin fresh play reads 'hand' (nothing owed).
function dragDrawnFromDeck(host, cardId) {
  const zone = host._authMirror?.getState?.()?.cards?.[cardId]?.zone;
  return zone === 'spellbook' || zone === 'atlas';
}

/**
 * Mirror of commitHandDragToTable: the user dragged a card from the
 * table and the cursor has crossed into the hand area. End the 3D
 * drag (removing the card from the table) and start the hand-drag
 * state so the user can keep moving without releasing the mouse.
 */
export function handOffDragToHand(host, cardInstance, event) {
  // A unit-hand drag may NEVER land in the player hand. Keyed off the
  // explicit drag marker (not _castableOnlyBy, which is wiped by the
  // table reset) so the player-hand splice below can't run and duplicate
  // the spell into both hands.
  if (host._unitHandDrag) {
    host.returnDragToUnitHand(cardInstance);
    return;
  }
  // End the 3D drag cleanly. Release any drag-mates back to physics
  // so only the primary card leaves the table.
  const members = host.dragging?.members || [];
  // A fresh-play card is one that was only staged for a drag (from
  // hand or pile draw) and was never committed. Dragging it back
  // into the hand cancels the play. No log entries are fired.
  const wasFreshPlay = host.dragging?.freshPlayIds?.has(cardInstance.id) || false;
  // Snapshot face-down BEFORE removeCardFromTable, which calls
  // resetCardOnLeavePlay → wipes faceDown. The log line below relies
  // on this to keep face-down identity hidden from the opponent.
  const wasFaceDown = !!cardInstance.faceDown;
  // Drag-mates not bound for the hand are released by the same
  // tickPhysicsSync local-owned branch that handles regular drops:
  // when host.dragging is cleared, the card is no longer in the
  // dragging set and the next frame fires _presenceRelease.
  host.dragging = null;
  host.clearCemeteryDragHover();
  host.clearDeckDragHover();

  // Remove from the table (broadcasts card:remove to opponent) but
  // skip sendToHand's append. We insert at the cursor position.
  // For fresh plays, mark the removal as `staged` so the site mana
  // bump and affinity recalc (which never happened during staging)
  // aren't incorrectly undone here.
  host.removeCardFromTable(cardInstance, true, { staged: wasFreshPlay });

  // Compute target insertion index from cursor X using the same
  // arc geometry as onHandDragMove so the card slides into the
  // position the user is hovering over.
  const hand = document.querySelector('[data-tutorial="board-hand"]');
  const rect = hand ? hand.getBoundingClientRect() : null;

  // v1.112. Route the picked-up card back into its OWNER's hand,
  // not the active player's. In casual sandbox the active player
  // toggles with Tab; without owner-based routing, a card lifted
  // off the table after a turn pass would land in the wrong hand.
  const handKey = host._handStateKeyForCard(cardInstance);
  const currentCards = host.state[handKey];
  let insertIdx = currentCards.length;
  if (rect && currentCards.length > 0) {
    const N = currentCards.length + 1; // +1 for the card being inserted
    const s = host.state.canvasScale;
    const arcRadius = Math.round(1800 * s);
    const maxAPC = N <= 3 ? 3 : N <= 5 ? 3 : N <= 8 ? 3 : N <= 12 ? 2 : 1.2;
    const tArcMax = N <= 5 ? 15 : N <= 8 ? 22 : N <= 12 ? 22 : 15;
    const apc = Math.min(maxAPC, tArcMax / Math.max(N - 1, 1));
    const totalArc = apc * (N - 1);

    const centerX = rect.left + rect.width / 2;
    const mouseX = event.clientX - centerX;

    let closestIdx = 0;
    let closestDist = Infinity;
    for (let i = 0; i < N; i++) {
      const angleDeg = N === 1 ? 0 : -totalArc / 2 + i * apc;
      const cardX = arcRadius * Math.sin(angleDeg * Math.PI / 180);
      const dist = Math.abs(mouseX - cardX);
      if (dist < closestDist) { closestDist = dist; closestIdx = i; }
    }
    insertIdx = closestIdx;
  }

  host.setState((state) => {
    const next = [...(state[handKey] || [])];
    next.splice(insertIdx, 0, cardInstance);
    return { [handKey]: next, handDragIndex: insertIdx, hoveredHandIndex: -1 };
  });
  // Only log when the card was actually on the table before. A
  // fresh-play cancel (played a card out of hand or drew from a
  // pile, then dragged it back) was never really "on the board",
  // so no log entry is needed.
  if (!wasFreshPlay) {
    const label = wasFaceDown ? msg('a face-down card') : (cardInstance.name || msg('a card'));
    host.logAction('Picked up {card} to hand', { card: label });
    // Auth zones: a committed board card dragged back returns to its OWNER's
    // hand. Emit the move so the server records it. Without it the card is
    // orphaned in the registry and vanishes (same bug as the menu/hotkey
    // sendToHand path). A fresh-play CANCEL skips this: that card was never
    // committed to 'play' server-side, so it is still in the owner's hand.
    if (host._authZonesEnabled?.()) host._emitMoveToZone(cardInstance.id, 'hand');
  } else if (host._authZonesEnabled?.()) {
    // A card dragged OFF a deck and dropped straight into the hand is a manual
    // draw (valkenhall-qa#158). drawCardToDrag only moved the local render, so
    // the server still holds the card in the deck; without this move the next
    // diff re-seats it on top of the deck and drops it from the hand. The
    // manualDraw mark is what lets the server count it as the start-phase
    // draw. drawCardToDrag already logged the draw at drag start.
    if (dragDrawnFromDeck(host, cardInstance.id)) {
      host._emitMoveToZone(cardInstance.id, 'hand', { manualDraw: true });
    }
  }

  host.handDrag = {
    card: cardInstance,
    originalIndex: insertIdx,
    startX: event.clientX,
    startY: event.clientY,
    handPosition: 'center',
    containerRect: rect,
    committed: false,
  };

  document.addEventListener('mousemove', host.onHandDragMove);
  document.addEventListener('mouseup', host.onHandDragEnd);
}
